/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-computed-properties-in-native-classes, ember/no-get */
import Model, {
  attr,
  belongsTo,
  type AsyncBelongsTo,
} from '@warp-drive/legacy/model';
import { alias } from '@ember/object/computed';
import { isEmpty } from '@ember/utils';
import type { Type } from '@warp-drive/core/types/symbols';
import { fragment } from 'ember-data-model-fragments/attributes';
import type { EnumValue } from '@linc-technologies/linced-common/enums/base-enum';
import Jurisdiction from '@linc-technologies/linced-common/enums/jurisdiction';
import NzService from 'linced-admin/enums/enrolment-nz-service';
import type CredentialNz from 'linced-admin/models/enrolments/credential-nz';
import type School from 'linced-admin/models/school';

// Proto: enrolments.Credential.
export default class Credential extends Model {
  declare [Type]: 'enrolments/credential';

  // createTime is when the resource was created in seconds from the epoch.
  @attr('number')
  declare createTime: number | undefined;

  // updateTime is the last time the resource was modified in seconds from
  // the epoch.
  @attr('number')
  declare updateTime: number | undefined;

  // schoolId is the unique identifier of the school.
  @belongsTo('school', { async: true, inverse: null })
  declare schoolId: AsyncBelongsTo<School>;

  @alias('schoolId')
  declare school: AsyncBelongsTo<School>;

  // campusId is the unique identifier of the School's Campus.
  @attr('string', { defaultValue: '' })
  declare campusId: string;

  // enabled provides a switch to disable the use of the stored credentials.
  @attr('boolean', { defaultValue: false })
  declare enabled: boolean;

  // jurisdiction maps the credential to a jurisdictional educational
  // authority.
  @attr('number', { defaultValue: 0 })
  declare jurisdiction: EnumValue<typeof Jurisdiction> | 0;

  // nzMoe provides the container to authenticate and authorize to New Zealand
  // jurisdictional services.
  @fragment('enrolments/credential-nz')
  declare nzMoe: CredentialNz | null;

  /**
   * setIntegrationPassword configures a jurisidictional credential for a
   * school.
   *
   * @param {integer} type - the type of password being set.
   * @param {string}  password - The password to be set.
   * @param {integer} [jurisdiction] - The school's jurisdiction.
   */
  setIntegrationPassword(
    type: number,
    password: string,
    jurisdiction?: number,
  ) {
    if (isEmpty(jurisdiction)) {
      jurisdiction = this.jurisdiction;
    }

    switch (jurisdiction) {
      case Jurisdiction.NZ_MOE:
        this.setNzMoePassword(type, password);
        break;
    }
  }

  /**
   * setNzMoePassword configures integration passwords for New Zealand
   * jurisdiction.
   *
   * @param {integer} type - the type of password being set.
   * @param {string}  password - The password to be set.
   */
  setNzMoePassword(type: number, password: string) {
    const nzMoe = {
      enrolId: this.get('school.jurisdictionalId'),
      password: password,
      service: 0,
    };

    switch (type) {
      case NzService.DEX:
        nzMoe.service = NzService.DEX;
        this.set('nzMoe', nzMoe);
    }
  }
}
